import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  TablePagination,
  Snackbar,
  Alert,
  Backdrop,
} from '@mui/material';
import { TableRowSkeleton, DocumentCardSkeleton } from '../components/LoadingSkeleton';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { OrganizationMember, Department, Permission } from '../types';
import { getOrganizationUsers, deleteUser, updateUser, inviteUser, getOrganizationRoles, getFirmProfile, updateFirmProfile, createFirmProfile, getDepartments, createDepartment, getPermissions } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ flexGrow: 1, p: 0, pt: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

const AdminPanel: React.FC = () => {
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Read tab from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 2) { // Adjust based on number of tabs
        setTabValue(tabIndex);
      }
    }
  }, [location.search]);
  const { user, currentOrganization } = useAuth();
  const organizationId = currentOrganization?.id;
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterIsActive, setFilterIsActive] = useState<boolean | null>(null);
  const [users, setUsers] = useState<OrganizationMember[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [userToDelete, setUserToDelete] = useState<OrganizationMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [isInvitingUser, setIsInvitingUser] = useState(false);
  const [newUserRole, setNewUserRole] = useState('');
  const [newUserDepartment, setNewUserDepartment] = useState<number | ''>('');
  const [resendingInvitation, setResendingInvitation] = useState<string | null>(null);
  const [userToEdit, setUserToEdit] = useState<OrganizationMember | null>(null);
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserName, setEditUserName] = useState('');
  const [editUserOldPassword, setEditUserOldPassword] = useState('');
  const [editUserNewPassword, setEditUserNewPassword] = useState('');
  const [editUserConfirmNewPassword, setEditUserConfirmNewPassword] = useState('');
  const [editUserRole, setEditUserRole] = useState('');
  const [editUserDepartment, setEditUserDepartment] = useState<number | ''>('');
    const [editUserPermission, setEditUserPermission] = useState<string[]>([]);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [firmName, setFirmName] = useState('');
  const [sectorFocus, setSectorFocus] = useState<string[]>([]);
  const [geographicFocus, setGeographicFocus] = useState<string[]>([]);
  const [investmentSizeMin, setInvestmentSizeMin] = useState<number | ''>(0);
  const [investmentSizeMax, setInvestmentSizeMax] = useState<number | ''>(0);
  const [holdingPeriodTarget, setHoldingPeriodTarget] = useState<number | ''>(5);
  const [holdingPeriodUnit, setHoldingPeriodUnit] = useState('years');
  const [exitStrategyPreferences, setExitStrategyPreferences] = useState<{ value: string; label: string; }[]>([]);
  const [saving, setSaving] = useState(false);
  const [isFetchingFirmProfile, setIsFetchingFirmProfile] = useState(false);
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [totalAum, setTotalAum] = useState(0);
  const [targetAum, setTargetAum] = useState(0);
  const [contactPerson, setContactPerson] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [firmProfileError, setFirmProfileError] = useState<string | null>(null);
  const [firmProfileExists, setFirmProfileExists] = useState(true);
  const [firmProfileMessage, setFirmProfileMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [openDepartmentDialog, setOpenDepartmentDialog] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [isCreatingDepartment, setIsCreatingDepartment] = useState(false);
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false);
    const [permissions, setPermissions] = useState<Permission[]>([]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset page when tab changes
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset page when rows per page changes
  };

  const saveFirmProfile = async () => {
    setSaving(true);
    try {
      if (!firmProfileExists) { // If profile does not exist, create it
        await createFirmProfile({
          firm_name: firmName,
          description,
          website,
          total_aum: totalAum,
          target_aum: targetAum,
          contact_person: contactPerson,
          contact_email: contactEmail,
          contact_phone: contactPhone,
          address,
          sector_focus: sectorFocus,
          geographic_focus: geographicFocus,
          investment_size_min: investmentSizeMin,
          investment_size_max: investmentSizeMax,
          holding_period_target: holdingPeriodTarget,
          holding_period_unit: holdingPeriodUnit,
          exit_strategy_preferences: exitStrategyPreferences.map(p => p.value),
        });
      } else { // Otherwise, update existing profile
        await updateFirmProfile({
          firm_name: firmName,
          description,
          website,
          total_aum: totalAum,
          target_aum: targetAum,
          contact_person: contactPerson,
          contact_email: contactEmail,
          contact_phone: contactPhone,
          address,
          sector_focus: sectorFocus,
          geographic_focus: geographicFocus,
          investment_size_min: investmentSizeMin,
          investment_size_max: investmentSizeMax,
          holding_period_target: holdingPeriodTarget,
          holding_period_unit: holdingPeriodUnit,
          exit_strategy_preferences: exitStrategyPreferences.map(p => p.value),
        });
      }
      setFirmProfileError(null); // Clear any previous errors on success
      setFirmProfileMessage({ text: 'Firm profile saved successfully!', type: 'success' });
    } catch (error: any) {
      console.error('Failed to save firm profile:', error);
      setFirmProfileError(error.response?.data?.message || 'Failed to save firm profile. Please try again.');
      setFirmProfileMessage({ text: error.response?.data?.message || 'Failed to save firm profile. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const commonSectors = ['Technology', 'Healthcare', 'Finance', 'Real Estate'];
  const commonRegions = ['North America', 'Europe', 'Asia', 'South America'];


  const handleInviteUser = async () => {
    if (!newUserRole) {
      // Optionally, show an error message to the user
      console.error('Please select a role for the new user.');
      return;
    }
    setIsInvitingUser(true);
    try {
      if (!organizationId) {
        console.error('Organization ID not found');
        return;
      }

      await inviteUser(organizationId, {
        email: newUserEmail,
        role: newUserRole,
        department_id: newUserDepartment || undefined,
      });
      fetchUsers();
      handleCloseUserDialog();
      setNewUserEmail('');
    } catch (error) {
      console.error('Failed to invite user:', error);
    } finally {
      setIsInvitingUser(false);
    }
  };

  const handleResendInvitation = async (member: OrganizationMember) => {
    setResendingInvitation(member.user_id);
    try {
      if (!organizationId) {
        console.error('Organization ID not found');
        return;
      }

      await inviteUser(organizationId, {
        email: member.user_email,
        role: member.role,
      });
      // Optionally, show a success notification here
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      // Optionally, show an error notification here
    } finally {
      setResendingInvitation(null);
    }
  };

  const handleUpdateUser = async () => {
    if (userToEdit) {
      setIsUpdatingUser(true);
      try {
        // Prevent users from changing their own role
        if (userToEdit.user_id === user?.id && userToEdit.role !== editUserRole) {
          console.error("You cannot change your own role.");
          // Optionally, show a user-facing error message here
          return;
        }

        await updateUser(userToEdit.user_id, {
          name: editUserName,
          email: editUserEmail,
          old_password: editUserOldPassword,
          new_password: editUserNewPassword,
          organization_role: editUserRole,
          department_id: editUserDepartment || undefined,
          permission_ids: editUserPermission,
        });
        
        setUsers(users.map(member =>
          member.user_id === userToEdit.user_id
            ? { ...member, user: { ...member.user, name: editUserName }, user_email: editUserEmail, role: editUserRole }
            : member
        ));
        
        handleCloseEditUserDialog();
      } catch (error) {
        console.error('Failed to update user:', error);
      } finally {
        setIsUpdatingUser(false);
      }
    }
  };
  
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!organizationId) {
        console.error('Organization ID not found');
        return;
      }
      const params: { role?: string; is_active?: boolean; search?: string; page?: number; size?: number } = {};
      if (filterRole !== 'all') params.role = filterRole;
      if (filterIsActive !== null) params.is_active = filterIsActive;
      if (searchTerm) params.search = searchTerm;
      params.page = page + 1; // API is 1-indexed, Material-UI is 0-indexed
      params.size = rowsPerPage;
      
      const fetchedUsersResponse = await getOrganizationUsers(organizationId, params);
      setUsers(fetchedUsersResponse.members);
      setTotalUsers(fetchedUsersResponse.total);
    } catch (error: any) {
      console.error('Failed to fetch users:', error.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, filterRole, filterIsActive, searchTerm, page, rowsPerPage, setUsers, setTotalUsers, organizationId]);


  const fetchRoles = useCallback(async () => {
    try {
      const fetchedRoles = await getOrganizationRoles();
      if (!fetchedRoles.includes('Reviewer')) {
        fetchedRoles.push('Reviewer');
      }
      setRoles(fetchedRoles);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  }, [setRoles]);


  const fetchFirmProfile = async () => {
    setIsFetchingFirmProfile(true);
    try {
      const data = await getFirmProfile();
      setFirmName(data.firm_name);
      setDescription(data.description);
      setWebsite(data.website);
      setTotalAum(data.total_aum);
      setTargetAum(data.target_aum);
      setContactPerson(data.contact_person);
      setContactEmail(data.contact_email);
      setContactPhone(data.contact_phone);
      setAddress(data.address);
      setSectorFocus(data.sector_focus);
      setGeographicFocus(data.geographic_focus);
      setInvestmentSizeMin(data.investment_size_min != null && !isNaN(parseFloat(data.investment_size_min)) ? parseFloat(data.investment_size_min) : '');
      setInvestmentSizeMax(data.investment_size_max != null && !isNaN(parseFloat(data.investment_size_max)) ? parseFloat(data.investment_size_max) : '');
      setHoldingPeriodTarget(data.holding_period_target != null && !isNaN(Number(data.holding_period_target)) ? Number(data.holding_period_target) : '');
      setHoldingPeriodUnit(data.holding_period_unit);
      setExitStrategyPreferences(data.exit_strategy_preferences.map((value: string) => ({ value, label: value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) })));
      setFirmProfileExists(true); // Profile found, set to true
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        // If profile not found, initialize with empty values and set firmProfileExists to false
        setFirmName('');
        setDescription('');
        setWebsite('');
        setTotalAum(0);
        setTargetAum(0);
        setContactPerson('');
        setContactEmail('');
        setContactPhone('');
        setAddress('');
        setSectorFocus([]);
        setGeographicFocus([]);
        setInvestmentSizeMin(0);
        setInvestmentSizeMax(0);
        setHoldingPeriodTarget(5);
        setHoldingPeriodUnit('years');
        setExitStrategyPreferences([]);
        setFirmProfileError(null); // Clear any previous errors
        setFirmProfileExists(false); // Set to false as profile was not found
      } else {
        console.error('Failed to fetch firm profile:', error);
        setFirmProfileError('Failed to load firm profile. Please try again.');
        setFirmProfileExists(true); // Assume it exists if not a 404, but failed for other reasons
      }
    } finally {
      setIsFetchingFirmProfile(false);
    }
  };

  const fetchDepartments = useCallback(async () => {
    setIsDepartmentsLoading(true);
    try {
      const fetchedDepartments = await getDepartments();
      setDepartments(fetchedDepartments);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    } finally {
      setIsDepartmentsLoading(false);
    }
  }, [setDepartments]);

   const fetchPermissions = useCallback(async () => {
    if (!organizationId) return;
    try {
      const fetchedPermissions = await getPermissions(organizationId);
      setPermissions(fetchedPermissions);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  }, [organizationId]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchUsers();
      fetchRoles();
      fetchDepartments();
      fetchPermissions();
    } else if (tabValue === 1) {
      fetchFirmProfile();
    } else if (tabValue === 2) {
      fetchDepartments();
    }
  }, [filterRole, filterIsActive, searchTerm, tabValue, page, rowsPerPage, fetchUsers, fetchRoles, fetchDepartments, fetchPermissions]);

  const handleOpenDepartmentDialog = () => {
    setOpenDepartmentDialog(true);
  };

  const handleCloseDepartmentDialog = () => {
    setOpenDepartmentDialog(false);
    setNewDepartmentName('');
  };

  const handleCreateDepartment = async () => {
    if (!newDepartmentName.trim()) {
      return;
    }
    setIsCreatingDepartment(true);
    try {
      await createDepartment({ name: newDepartmentName });
      fetchDepartments();
      handleCloseDepartmentDialog();
    } catch (error) {
      console.error('Failed to create department:', error);
    } finally {
      setIsCreatingDepartment(false);
    }
  };

  const handleDeleteUser = async () => {
    if (userToDelete) {
      setIsDeletingUser(true);
      try {
        // Simulate a network request
        await new Promise(resolve => setTimeout(resolve, 2000));
        await deleteUser(userToDelete.user_id);
        setUsers(users.filter(member => member.id !== userToDelete.id));
        setUserToDelete(null);
      } catch (error) {
        console.error('Failed to delete user:', error);
      } finally {
        setIsDeletingUser(false);
      }
    }
  };

  const openDeleteConfirmation = (user: OrganizationMember) => {
    setUserToDelete(user);
  };

  const closeDeleteConfirmation = () => {
    setUserToDelete(null);
  };

  const handleOpenUserDialog = () => {
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
  };

  const handleOpenEditUserDialog = (user: OrganizationMember) => {
    setUserToEdit(user);
    setEditUserEmail(user.user_email);
    setEditUserName(user.user.name);
    setEditUserRole(user.role);
    setEditUserDepartment(user.department?.id || '');
    setEditUserPermission(user.permissions?.map(p => p.id) || []);
  };

  const handleCloseEditUserDialog = () => {
    setUserToEdit(null);
    setEditUserEmail('');
    setEditUserName('');
    setEditUserOldPassword('');
    setEditUserNewPassword('');
    setEditUserConfirmNewPassword('');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const getRoleColor = (role: string): 'error' | 'primary' | 'default' => {
    switch (role) {
      case 'Owner':
        return 'error';
      case 'Admin':
        return 'primary';
      case 'Reviewer':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isUpdatingUser}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Admin Panel</Typography>
      </Paper>
      <Paper sx={{ width: '100%', height: '85vh', display: 'flex', flexDirection: 'column'}}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin panel tabs">
            <Tab label="User Management" {...a11yProps(0)} />
            <Tab label="Firm Profile" {...a11yProps(1)} />
            <Tab label="Departments" {...a11yProps(2)} />
            {/* <Tab label="Template Library" {...a11yProps(3)} /> */}
            {/* <Tab label="Audit Logs" {...a11yProps(3)} /> */}
            {/* <Tab label="Settings" {...a11yProps(2)} /> */}
          </Tabs>
        </Box>

        {/* User Management Tab */}
        <TabPanel value={tabValue} index={0}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(85vh - 120px)' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ px: 3, pb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt:1}}>
              <Typography variant="h5">User Management</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenUserDialog}
                disabled={currentOrganization.organization_role !== 'Owner' && currentOrganization.organization_role !== 'Admin'}
              >
                Invite User
              </Button>
            </Box>

            <Box sx={{ display: 'flex', mb: 3, gap: 2 }}>
              <TextField
                placeholder="Search by name or email..."
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ flexGrow: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="role-filter-label">Role</InputLabel>
                <Select
                  labelId="role-filter-label"
                  id="role-filter"
                  value={filterRole}
                  label="Role"
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  value={filterIsActive === null ? 'all' : filterIsActive ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'all') {
                      setFilterIsActive(null);
                    } else if (value === 'active') {
                      setFilterIsActive(true);
                    } else {
                      setFilterIsActive(false);
                    }
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(85vh - 250px)', overflowY: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Name</TableCell>
                    <TableCell sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Email</TableCell>
                    <TableCell sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Role</TableCell>
                    <TableCell sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Department</TableCell>
                    <TableCell sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Joined At</TableCell>
                    <TableCell sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Last Login</TableCell>
                    <TableCell sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Status</TableCell>
                    <TableCell align="right" sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {users.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>{member.user.name}</TableCell>
                        <TableCell>{member.user_email}</TableCell>
                        <TableCell>
                          <Chip
                            label={member.role}
                            color={getRoleColor(member.role)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{member.department?.name}</TableCell>
                        <TableCell>{new Date(member.joined_at).toLocaleDateString()}</TableCell>
                        <TableCell>{member.user.last_login ? new Date(member.user.last_login).toLocaleString() : 'N/A'}</TableCell>
                        <TableCell>
                          {member.invitation_status === 'Pending' ? (
                            <Chip label="Invited" color="warning" size="small" />
                          ) : (
                            <Chip
                              label={member.is_active ? "Active" : "Inactive"}
                              color={member.is_active ? "success" : "default"}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {member.invitation_status === 'Pending' ? (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={resendingInvitation === member.user_id ? <CircularProgress size={16} /> : <SendIcon />}
                              onClick={() => handleResendInvitation(member)}
                              disabled={resendingInvitation === member.user_id}
                            >
                              {resendingInvitation === member.user_id ? 'Sending...' : 'Send Invitation'}
                            </Button>
                          ) : (
                            <>
                              <IconButton size="small" sx={{ mr: 1 }} onClick={() => handleOpenEditUserDialog(member)} disabled={(currentOrganization.organization_role !== 'Owner' && currentOrganization.organization_role !== 'Admin') || member.user_id === user?.id}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => openDeleteConfirmation(member)} disabled={(currentOrganization.organization_role !== 'Owner' && currentOrganization.organization_role !== 'Admin') || member.user_id === user?.id}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* Add User Dialog */}
            <Dialog open={openUserDialog} onClose={handleCloseUserDialog}>
              <DialogTitle>Invite New User</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                  Enter the details for the new user. They will receive an email invitation to set up their account.
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  id="email"
                  label="Email Address"
                  type="email"
                  fullWidth
                  variant="outlined"
                  sx={{ mb: 2 }}
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
               <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                 <InputLabel id="new-user-role-label">Role</InputLabel>
                 <Select
                   labelId="new-user-role-label"
                   id="new-user-role"
                   value={newUserRole}
                   onChange={(e) => setNewUserRole(e.target.value)}
                   label="Role"
                 >
                   {roles
                     .filter((role) => role !== 'Owner')
                     .map((role) => (
                       <MenuItem key={role} value={role}>
                         {role}
                       </MenuItem>
                     ))}
                 </Select>
               </FormControl>
               <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                 <InputLabel id="new-user-department-label">Department</InputLabel>
                 <Select
                   labelId="new-user-department-label"
                   id="new-user-department"
                   value={newUserDepartment}
                   onChange={(e) => setNewUserDepartment(e.target.value as number | '')}
                   label="Department"
                 >
                   <MenuItem value="">
                     <em>None</em>
                   </MenuItem>
                   {departments.map((department) => (
                     <MenuItem key={department.id} value={department.id}>
                       {department.name}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </DialogContent>
             <DialogActions>
               <Button onClick={handleCloseUserDialog}>Cancel</Button>
                <Button onClick={handleInviteUser} variant="contained" disabled={isInvitingUser}>
                  {isInvitingUser ? <CircularProgress size={24} color="inherit" /> : 'Invite User'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </TabPanel>

        {/* Edit User Dialog */}
        <Dialog open={!!userToEdit} onClose={handleCloseEditUserDialog}>
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Update the details for {userToEdit?.user_name}.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="edit-name"
              label="Name"
              type="text"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              value={editUserName}
              onChange={(e) => setEditUserName(e.target.value)}
            />
            <TextField
              margin="dense"
              id="edit-email"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              value={editUserEmail}
              onChange={(e) => setEditUserEmail(e.target.value)}
              disabled
            />
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel id="edit-user-role-label">Role</InputLabel>
              <Select
                labelId="edit-user-role-label"
                id="edit-user-role"
                value={editUserRole}
                onChange={(e) => setEditUserRole(e.target.value)}
                label="Role"
              >
                {roles
                  .filter((role) => role !== 'Owner')
                  .map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel id="edit-user-department-label">Department</InputLabel>
              <Select
                labelId="edit-user-department-label"
                id="edit-user-department"
                value={editUserDepartment}
                onChange={(e) => setEditUserDepartment(e.target.value as number | '')}
                label="Department"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              id="edit-user-permission"
              options={permissions}
              getOptionLabel={(option) => option.name}
              value={permissions.filter(p => editUserPermission.includes(p.id))}
              onChange={(_, newValue) => {
                setEditUserPermission(newValue.map(v => v.id));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Permissions"
                  placeholder="Select permissions"
                />
              )}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="edit-old-password"
              label="Old Password"
              type="password"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              value={editUserOldPassword}
              onChange={(e) => setEditUserOldPassword(e.target.value)}
            />
            <TextField
              margin="dense"
              id="edit-new-password"
              label="New Password"
              type="password"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              value={editUserNewPassword}
              onChange={(e) => setEditUserNewPassword(e.target.value)}
              error={editUserNewPassword !== editUserConfirmNewPassword}
              helperText={editUserNewPassword !== editUserConfirmNewPassword ? "Passwords do not match" : "Leave blank to keep the current password."}
            />
            <TextField
              margin="dense"
              id="edit-confirm-new-password"
              label="Confirm New Password"
              type="password"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              value={editUserConfirmNewPassword}
              onChange={(e) => setEditUserConfirmNewPassword(e.target.value)}
              error={editUserNewPassword !== editUserConfirmNewPassword}
              helperText={editUserNewPassword !== editUserConfirmNewPassword ? "Passwords do not match" : ""}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditUserDialog}>Cancel</Button>
            <Button onClick={handleUpdateUser} variant="contained" disabled={isUpdatingUser}>
              {isUpdatingUser ? <CircularProgress size={24} color="inherit" /> : 'Update User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={!!userToDelete}
          onClose={closeDeleteConfirmation}
        >
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete {userToDelete?.user_name}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteConfirmation} disabled={isDeletingUser}>Cancel</Button>
            <Button onClick={handleDeleteUser} color="error" disabled={isDeletingUser}>
              {isDeletingUser ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Template Library Tab */}
        {/*
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Template Library</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenTemplateDialog}
            >
              Add Template
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableRowSkeleton key={`template-skeleton-${index}`} columns={5} />
                  ))
                ) : (
                  mockTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>{template.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={template.type}
                          color={getTemplateTypeColor(template.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{users.find(member => member.user_id === template.createdBy)?.user_name || 'Unknown'}</TableCell>
                      <TableCell>{new Date(template.createdDate).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" sx={{ mr: 1 }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ mr: 1 }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add Template Dialog *}
          <Dialog open={openTemplateDialog} onClose={handleCloseTemplateDialog}>
            <DialogTitle>Add New Template</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Create a new template that can be used across the platform.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="template-name"
                label="Template Name"
                type="text"
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="template-type-label">Template Type</InputLabel>
                <Select
                  labelId="template-type-label"
                  id="template-type"
                  value={selectedTemplateType}
                  label="Template Type"
                  onChange={handleTemplateTypeChange}
                >
                  <MenuItem value={TemplateType.Clause}>Clause</MenuItem>
                  <MenuItem value={TemplateType.Report}>Report</MenuItem>
                  <MenuItem value={TemplateType.Memo}>Memo</MenuItem>
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                id="template-content"
                label="Template Content"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseTemplateDialog}>Cancel</Button>
              <Button onClick={handleCloseTemplateDialog} variant="contained">Add Template</Button>
            </DialogActions>
          </Dialog>
        </TabPanel>
        *}

        {/* Audit Logs Tab */}
        {/*
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Audit Logs</Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              Export Logs
            </Button>
          </Box>

          <Box sx={{ display: 'flex', mb: 3, gap: 2 }}>
            <TextField
              placeholder="Search logs..."
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ flexGrow: 1 }}
            />
            <TextField
              placeholder="User ID"
              size="small"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <TextField
              placeholder="Entity Type"
              size="small"
              value={filterEntityType}
              onChange={(e) => setFilterEntityType(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="action-filter-label">Action</InputLabel>
              <Select
                labelId="action-filter-label"
                id="action-filter"
                value={filterAuditAction}
                label="Action"
                onChange={handleFilterAuditAction}
              >
                <MenuItem value="all">All Actions</MenuItem>
                <MenuItem value="create">Create</MenuItem>
                <MenuItem value="update">Update</MenuItem>
                <MenuItem value="delete">Delete</MenuItem>
                <MenuItem value="view">View</MenuItem>
                <MenuItem value="login">Login</MenuItem>
                <MenuItem value="logout">Logout</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <TableRowSkeleton key={`audit-skeleton-${index}`} columns={4} />
                  ))
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                      <TableCell>{users.find(member => member.user_id === log.user_id)?.user_name || 'Unknown'}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          color={getAuditActionColor(log.action)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.description}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        *}


        {/* Settings Tab */}
        {/*
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h5" gutterBottom>System Settings</Typography>
          
          {isLoading || !settings ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <MetricCardSkeleton />
              <MetricCardSkeleton />
            </Box>
          ) : (
            <>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <Card.Content sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Security Settings</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.require_email_verification}
                        onChange={(e) => setSettings({ ...settings, require_email_verification: e.target.checked })}
                      />
                    }
                    label="Require Email Verification"
                  />
                  <Typography variant="body2" color="text.secondary">
                    New users must verify their email address before they can log in.
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                    <Switch
                        checked={settings.allow_public_signup}
                        onChange={(e) => setSettings({ ...settings, allow_public_signup: e.target.checked })}
                      />
                    }
                    label="Allow Public Signup"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Allow new users to sign up for an account.
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.enforce_2fa}
                        onChange={(e) => setSettings({ ...settings, enforce_2fa: e.target.checked })}
                      />
                    }
                    label="Two-Factor Authentication"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Require two-factor authentication for all admin users
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <TextField
                    label="Session Timeout"
                    value={settings.session_timeout}
                    onChange={(e) => setSettings({ ...settings, session_timeout: e.target.value })}
                    fullWidth
                    variant="outlined"
                    helperText="e.g., 30m, 1h, 24h"
                  />
                </Box>
                
                <Button
                  variant="contained"
                  onClick={() => handleUpdateSettings({
                    require_email_verification: settings.require_email_verification,
                    allow_public_signup: settings.allow_public_signup,
                    enforce_2fa: settings.enforce_2fa,
                    session_timeout: settings.session_timeout,
                  })}
                  disabled={isUpdatingSettings}
                >
                  {isUpdatingSettings ? <CircularProgress size={24} color="inherit" /> : 'Save Security Settings'}
                </Button>
                </Card.Content>
              </Card>
            </>
          )}
          
          <Card variant="outlined">
            <Card.Content sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Default Templates</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="default-clause-template-label">Default Clause Template</InputLabel>
              <Select
                labelId="default-clause-template-label"
                id="default-clause-template"
                defaultValue=""
                label="Default Clause Template"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {mockTemplates
                  .filter(template => template.type === TemplateType.Clause)
                  .map(template => (
                    <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="default-memo-template-label">Default Memo Template</InputLabel>
              <Select
                labelId="default-memo-template-label"
                id="default-memo-template"
                defaultValue=""
                label="Default Memo Template"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {mockTemplates
                  .filter(template => template.type === TemplateType.Memo)
                  .map(template => (
                    <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            
            <Button variant="contained">Save Template Settings</Button>
            </Card.Content>
          </Card>
        </TabPanel>
        *}

        {/* Firm Profile Tab */}
        <TabPanel value={tabValue} index={1}>
          {isFetchingFirmProfile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{
                height: 40,
                bgcolor: 'grey.300',
                borderRadius: 1,
                animation: 'pulse 1.5s ease-in-out infinite',
                width: '60%'
              }} />
              <Box sx={{
                height: 20,
                bgcolor: 'grey.300',
                borderRadius: 1,
                animation: 'pulse 1.5s ease-in-out infinite',
                width: '80%'
              }} />
              {Array.from({ length: 6 }).map((_, index) => (
                <DocumentCardSkeleton key={`firm-profile-skeleton-${index}`} />
              ))}
            </Box>
          ) : (
            <>
              <Box sx={{ px: 3, pb: 3 }}>
                {isFetchingFirmProfile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{
                      height: 40,
                      bgcolor: 'grey.300',
                      borderRadius: 1,
                      animation: 'pulse 1.5s ease-in-out infinite',
                      width: '60%'
                    }} />
                    <Box sx={{
                      height: 20,
                      bgcolor: 'grey.300',
                      borderRadius: 1,
                      animation: 'pulse 1.5s ease-in-out infinite',
                      width: '80%'
                    }} />
                    {Array.from({ length: 6 }).map((_, index) => (
                      <DocumentCardSkeleton key={`firm-profile-skeleton-${index}`} />
                    ))}
                  </Box>
                ) : (
                  <>
                    <Typography variant="h5" gutterBottom>🏢 Firm Profile</Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      Define your firm's basic investment parameters and strategy focus.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
                      <TextField
                        fullWidth
                        label="Firm Name"
                        value={firmName}
                        onChange={(e) => setFirmName(e.target.value)}
                        variant="outlined"
                      />
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>Sector Focus</Typography>
                        <Autocomplete
                          multiple
                          options={commonSectors}
                          value={sectorFocus}
                          onChange={(_, newValue) => setSectorFocus(newValue)}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select sectors" />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip label={option} {...getTagProps({ index })} />
                            ))
                          }
                        />
                      </Box>

                      <Box>
                        <Typography variant="subtitle1" gutterBottom>Geographic Focus</Typography>
                        <Autocomplete
                          multiple
                          options={commonRegions}
                          value={geographicFocus}
                          onChange={(_, newValue) => setGeographicFocus(newValue)}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select regions" />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip label={option} {...getTagProps({ index })} />
                            ))
                          }
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: '250px' }}>
                          <Typography variant="subtitle1" gutterBottom>Investment Size Range</Typography>
                          <TextField
                            fullWidth
                            label="Minimum (USD)"
                            value={investmentSizeMin}
                            onChange={(e) => setInvestmentSizeMin(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            type="number"
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            label="Maximum (USD)"
                            value={investmentSizeMax}
                            onChange={(e) => setInvestmentSizeMax(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            type="number"
                          />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: '250px' }}>
                          <Typography variant="subtitle1" gutterBottom>Holding Period Target</Typography>
                          <TextField
                            fullWidth
                            label="Target Period"
                            value={holdingPeriodTarget}
                            onChange={(e) => setHoldingPeriodTarget(e.target.value === '' ? '' : Number(e.target.value))}
                            type="number"
                            sx={{ mb: 2 }}
                          />
                          <FormControl fullWidth>
                            <Select
                              value={holdingPeriodUnit}
                              onChange={(e) => setHoldingPeriodUnit(e.target.value)}
                            >
                              <MenuItem value="months">Months</MenuItem>
                              <MenuItem value="years">Years</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="subtitle1" gutterBottom>Exit Strategy Preferences</Typography>
                        <Autocomplete
                          multiple
                          options={[
                            { value: 'ipo', label: 'IPO' },
                            { value: 'strategic_sale', label: 'Strategic Sale' },
                            { value: 'financial_buyer', label: 'Financial Buyer' },
                            { value: 'management_buyout', label: 'Management Buyout' },
                            { value: 'dividend_recap', label: 'Dividend Recapitalization' },
                            { value: 'refinancing', label: 'Refinancing' }
                          ]}
                          value={exitStrategyPreferences}
                          onChange={(_, newValue) => setExitStrategyPreferences(newValue)}
                          getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                          isOptionEqualToValue={(option, value) => {
                            if (typeof option === 'string' && typeof value === 'string') {
                              return option === value;
                            }
                            return (typeof option === 'string' ? option : option.value) === (typeof value === 'string' ? value : value.value);
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select exit strategies" />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                label={typeof option === 'string' ? option : option.label}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                        />
                      </Box>
                    </Box>

                    {firmProfileError && (
                      <Typography color="error" sx={{ mt: 2, textAlign: 'right' }}>
                        {firmProfileError}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveFirmProfile}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Firm Profile'}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}
        </TabPanel>

        {/* Departments Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt:1 }}>
              <Typography variant="h5">Departments</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDepartmentDialog}
              >
                Create Department
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(85vh - 250px)', overflowY: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isDepartmentsLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRowSkeleton key={`department-skeleton-${index}`} columns={1} />
                    ))
                  ) : (
                    departments.map((department) => (
                      <TableRow key={department.id}>
                        <TableCell>{department.name}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Create Department Dialog */}
        <Dialog open={openDepartmentDialog} onClose={handleCloseDepartmentDialog}>
          <DialogTitle>Create New Department</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter the name for the new department.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="department-name"
              label="Department Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newDepartmentName}
              onChange={(e) => setNewDepartmentName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDepartmentDialog}>Cancel</Button>
            <Button onClick={handleCreateDepartment} variant="contained" disabled={isCreatingDepartment}>
              {isCreatingDepartment ? <CircularProgress size={24} color="inherit" /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>



      </Paper>

      <Snackbar
        open={!!firmProfileMessage}
        autoHideDuration={6000}
        onClose={() => setFirmProfileMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setFirmProfileMessage(null)} severity={firmProfileMessage?.type} sx={{ width: '100%' }}>
          {firmProfileMessage?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default AdminPanel;
